import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCreateEntity } from 'src/features/entity/api/useCreateEntity';
import WaiveObligationsModal from 'src/features/membership/components/forms/WaiveObligationsModal';
import { useConvertActiveStay } from 'src/features/stay/api/useConvertActiveStay';
import { ACTIVE_STAY_RESOURCE } from 'src/features/stay/api/useGetActiveStay';
import { LIST_ACTIVE_STAY_PERIODS_RESOURCE } from 'src/features/stay/api/useListActiveStayPeriods';
import { LIST_ACTIVE_STAYS_RESOURCE } from 'src/features/stay/api/useListActiveStays';
import ConvertActiveStayToMembershipForm from 'src/features/stay/components/forms/ConvertActiveStayToMembershipForm';
import { ActiveStay } from 'src/features/stay/types';
import {
    convertActiveStayToMembershipFormSchema,
    ConvertActiveStayToMembershipFormSchemaValues,
} from 'src/features/stay/utils/formSchema';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface ConvertActiveStayToMembershipModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedActiveStay?: ActiveStay;
    children?: React.ReactNode;
}

function ConvertActiveStayToMembershipModal<TData>({
    selectedActiveStay,
    children,
}: ConvertActiveStayToMembershipModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
    const [pendingValues, setPendingValues] = React.useState<ConvertActiveStayToMembershipFormSchemaValues | null>(
        null,
    );

    const withValidationConfirmation = Boolean(
        selectedActiveStay?.total_amounts && selectedActiveStay?.total_amounts?.total_remaining_amount > 0,
    );

    const { mutate: createEntity } = useCreateEntity({});
    const { mutate, isPending } = useConvertActiveStay({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACTIVE_STAYS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [ACTIVE_STAY_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_ACTIVE_STAY_PERIODS_RESOURCE] });
            setIsModalOpen(false);

            toast({
                title: formatMessage(visitorMessages.convertedActiveStaySuccess),
                description: formatMessage(visitorMessages.convertedActiveStaySuccessDescription),
            });
        },
    });

    const defaultValues = React.useMemo(() => {
        return {
            active_stay: selectedActiveStay?.id ?? undefined,
            name: undefined,
            valid_from: undefined,
            valid_to: undefined,
            membership_type: undefined,
            max_allowed_spaces_count: undefined,
            owner: undefined,
            membership_allowed_times: undefined,
            entity: undefined,
            is_validated: false,
        };
    }, [selectedActiveStay]);

    const form = useForm<ConvertActiveStayToMembershipFormSchemaValues>({
        resolver: zodResolver(convertActiveStayToMembershipFormSchema),
        defaultValues,
    });

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const submit = React.useCallback(
        (values: ConvertActiveStayToMembershipFormSchemaValues, isValidated?: boolean) => {
            if (values.entity) {
                createEntity(
                    { parkingId, ...values.entity },
                    {
                        onSuccess: (entity) => {
                            const newValues = {
                                ...values,
                                owner: entity.id,
                            };
                            mutate({ ...newValues, is_validated: isValidated ?? false, parkingId });
                        },
                    },
                );
            } else mutate({ ...values, is_validated: isValidated ?? false, parkingId });
        },
        [mutate, createEntity, parkingId],
    );

    const onSubmit = React.useCallback(
        (values: ConvertActiveStayToMembershipFormSchemaValues) => {
            if (withValidationConfirmation) {
                setPendingValues(values);
                setIsConfirmOpen(true);
                return;
            }
            submit(values);
        },
        [submit, withValidationConfirmation],
    );

    const handleConfirm = React.useCallback(
        (isValidated: boolean) => {
            setIsConfirmOpen(false);
            if (pendingValues) submit(pendingValues, isValidated);
        },
        [pendingValues, submit],
    );

    return (
        <>
            <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
                <DialogTrigger asChild>{children}</DialogTrigger>
                <DialogContent>
                    <DialogHeader className="flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.MEMBER} />
                        <div>
                            <DialogTitle>{formatMessage(visitorMessages.convertActiveStay)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(visitorMessages.convertActiveStayDescription)}
                            </DialogDescription>
                        </div>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <ConvertActiveStayToMembershipForm form={form} onSubmit={onSubmit} />
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                <FormattedMessage {...generalMessages.cancel} />
                            </Button>
                        </DialogClose>
                        <Button
                            variant="default"
                            type="submit"
                            onClick={form.handleSubmit(onSubmit)}
                            className="flex-1"
                        >
                            {isPending && (
                                <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                            )}
                            {formatMessage(visitorMessages.convertActiveStay)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {withValidationConfirmation && (
                <WaiveObligationsModal
                    handleConfirm={handleConfirm}
                    isConfirmOpen={isConfirmOpen}
                    setIsConfirmOpen={setIsConfirmOpen}
                />
            )}
        </>
    );
}

export default ConvertActiveStayToMembershipModal;
